<template>
	<div v-if="isOpen" class="modal-overlay" @click="closeModal">
		<form class="modal-content" @click.stop @submit.prevent="onSave">
			<div class="modal-header">
				<h3>{{ label("title") }}</h3>
				<button type="button" class="close-btn" :disabled="saving" @click="closeModal">&times;</button>
			</div>

			<div class="modal-body">
				<div v-if="submitError" class="modal-error">{{ submitError }}</div>

				<div class="field">
					<span class="field-label">{{ label("user") }}</span>
					<ArkListboxDropdown
						:model-value="userProfileId"
						@update:model-value="handleUserChange"
						:options="userOptions"
						:multiple="false"
						:disabled="saving"
						:clearable="false"
						:searchable="true"
						:placeholder="label('userPlaceholder')"
						:no-options-text="label('noUsers')"
						:same-width="true"
					/>
				</div>

				<label class="field">
					<span class="field-label">{{ label("topic") }}</span>
					<input v-model.trim="topic" type="text" class="field-input" :disabled="saving" />
				</label>

				<label class="field">
					<span class="field-label">{{ label("titleLabel") }}</span>
					<input
						v-model.trim="title"
						type="text"
						class="field-input"
						:placeholder="label('titlePlaceholder')"
						:disabled="saving"
					/>
				</label>

				<label class="field">
					<span class="field-label">{{ label("message") }}</span>
					<textarea
						v-model.trim="message"
						class="field-input field-textarea"
						:placeholder="label('messagePlaceholder')"
						:disabled="saving"
					></textarea>
				</label>

				<label class="field">
					<span class="field-label">{{ label("projectId") }}</span>
					<input
						v-model.trim="projectId"
						type="text"
						class="field-input"
						:placeholder="label('projectIdPlaceholder')"
						:disabled="saving"
					/>
				</label>

				<label class="field">
					<span class="field-label">{{ label("customViewId") }}</span>
					<input
						v-model.trim="customViewId"
						type="text"
						class="field-input"
						:placeholder="label('customViewIdPlaceholder')"
						:disabled="saving"
					/>
				</label>

				<label class="field">
					<span class="field-label">{{ label("opportunityIds") }}</span>
					<input
						v-model.trim="opportunityIds"
						type="text"
						class="field-input"
						:placeholder="label('opportunityIdsPlaceholder')"
						:disabled="saving"
					/>
				</label>
			</div>

			<div class="modal-footer">
				<Button variant="secondary" size="m" type="button" :disabled="saving" @click="closeModal">
					{{ label("cancel") }}
				</Button>
				<Button variant="primary" size="m" type="submit" :disabled="saving || !canSave">
					{{ saving ? label("saving") : label("save") }}
				</Button>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import Button from "@/components/ui/Button.vue"

type UserOption = {
	value: number
	label: string
}

interface Props {
	isOpen: boolean
	userOptions: UserOption[]
	saving?: boolean
	submitError?: string
	translationKey?: string
}

interface Emits {
	(e: "close"): void
	(
		e: "save",
		payload: {
			userProfileId: number
			topic: string
			title: string
			message?: string
			projectId?: string
			customViewId?: string
			opportunityIds?: string[]
		}
	): void
}

const props = withDefaults(defineProps<Props>(), {
	saving: false,
	submitError: "",
	translationKey: "adminPage.createNotificationModal",
})

const emit = defineEmits<Emits>()
const { t } = useI18n()

const userProfileId = ref<number | null>(null)
const topic = ref("admin_manual_email")
const title = ref("")
const message = ref("")
const projectId = ref("")
const customViewId = ref("")
const opportunityIds = ref("")

const canSave = computed(() => userProfileId.value != null && title.value.trim() !== "" && topic.value.trim() !== "")

const label = (key: string) => t(`${props.translationKey}.${key}`)

watch(
	() => props.isOpen,
	open => {
		if (open) {
			userProfileId.value = null
			topic.value = "admin_manual_email"
			title.value = ""
			message.value = ""
			projectId.value = ""
			customViewId.value = ""
			opportunityIds.value = ""
		}
	}
)

const handleUserChange = (value: string | number | null | (string | number | null)[]) => {
	const selected = Array.isArray(value) ? value[0] : value
	userProfileId.value = selected == null ? null : Number(selected)
}

const closeModal = () => {
	if (!props.saving) {
		emit("close")
	}
}

const optionalText = (value: string): string | undefined => {
	const trimmed = value.trim()
	return trimmed === "" ? undefined : trimmed
}

const parsedOpportunityIds = (): string[] | undefined => {
	const ids = opportunityIds.value
		.split(",")
		.map(id => id.trim())
		.filter(Boolean)
	return ids.length > 0 ? ids : undefined
}

const onSave = () => {
	if (!canSave.value || userProfileId.value == null) return

	emit("save", {
		userProfileId: userProfileId.value,
		topic: topic.value.trim(),
		title: title.value.trim(),
		message: optionalText(message.value),
		projectId: optionalText(projectId.value),
		customViewId: optionalText(customViewId.value),
		opportunityIds: parsedOpportunityIds(),
	})
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
}

.modal-content {
	background: white;
	border-radius: 8px;
	width: 90%;
	max-width: 560px;
	max-height: 86vh;
	overflow-y: auto;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid #e0e0e0;
	background-color: #f8f9fa;

	h3 {
		margin: 0;
		color: #333;
	}

	.close-btn {
		background: none;
		border: none;
		font-size: 24px;
		cursor: pointer;
		color: #666;
		padding: 0;
		width: 30px;
		height: 30px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
}

.modal-body {
	display: grid;
	gap: 16px;
	padding: 20px;

	.modal-error {
		padding: 10px 12px;
		background-color: #ffebee;
		border: 1px solid #ffcdd2;
		border-radius: 4px;
		color: #d32f2f;
		font-size: 14px;
	}
}

.field {
	display: grid;
	gap: 6px;

	.field-label {
		font-weight: 600;
		color: #333;
	}

	.field-input {
		width: 100%;
		padding: 10px 12px;
		border: 1px solid #ccc;
		border-radius: 4px;
		font-size: 14px;

		&:focus {
			outline: none;
			border-color: #1976d2;
			box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
		}
	}

	.field-textarea {
		min-height: 96px;
		resize: vertical;
	}
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding: 20px;
	border-top: 1px solid #e0e0e0;
	background-color: #f8f9fa;
}
</style>
